import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  addWeeks,
  subWeeks,
  startOfWeek,
  addDays,
  isToday,
  isSameDay,
  differenceInMinutes,
  parse,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Users, Clock, User, UserPlus } from 'lucide-react';

import { useBookingsByDay, useClasses, DayBooking, TimeSlot } from '@/api/classes';
import { AddUserToClassDialog } from '@/components/classes/add-user-to-class-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/shared/page-header';

type StatusFilter = 'all' | 'confirmed' | 'cancelled' | 'pending' | 'active';

interface GroupedBooking {
  classId: string;
  timeSlotId: string;
  startTime: string;
  endTime: string;
  date: string;
  className: string;
  classLabel: string;
  staffMember?: { _id: string; name: string; surname: string; userType: string };
  participants: {
    _id: string;
    fullName: string;
    phoneNumber?: string;
    status: DayBooking['status'];
    createdAt: string;
  }[];
}

function getStatusVariant(status: DayBooking['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'confirmed':
    case 'active':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getStatusLabel(status: DayBooking['status']): string {
  switch (status) {
    case 'confirmed': return 'Confirmed';
    case 'active': return 'Active';
    case 'pending': return 'Pending';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
}

function getDuration(startTime: string, endTime: string, date: string): number {
  try {
    const start = parse(`${date} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date());
    const end = parse(`${date} ${endTime}`, 'yyyy-MM-dd HH:mm', new Date());
    return differenceInMinutes(end, start);
  } catch {
    return 0;
  }
}

function isSlotActiveOnDate(slot: TimeSlot, date: string, classDaysOfWeek?: number[]): boolean {
  if (slot.date) return slot.date === date;
  const targetDay = new Date(date + 'T00:00:00').getDay();
  const daysOfWeek = slot.daysOfWeek ?? classDaysOfWeek;
  if (daysOfWeek && daysOfWeek.length > 0) return daysOfWeek.includes(targetDay);
  return slot.applyEveryDay ?? false;
}

function BookingCard({ group }: { group: GroupedBooking }) {
  const { t } = useTranslation();
  const [addUserOpen, setAddUserOpen] = useState(false);
  const duration = getDuration(group.startTime, group.endTime, group.date);
  const confirmedCount = group.participants.filter(
    (p) => p.status === 'confirmed' || p.status === 'active'
  ).length;

  return (
    <>
      <Card className="overflow-hidden transition-shadow hover:shadow-md">
        <CardContent className="p-0">
          <div className="flex">
            {/* Time column */}
            <div className="flex flex-col items-center justify-start bg-muted/40 px-4 py-4 min-w-[80px] border-r">
              <span className="text-lg font-bold tabular-nums">{group.startTime}</span>
              <span className="text-xs text-muted-foreground mt-1">{group.endTime}</span>
            </div>

            {/* Content column */}
            <div className="flex-1 p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-semibold text-base leading-tight">{group.className}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs font-medium">
                      {group.classLabel}
                    </Badge>
                    {duration > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {duration} min
                      </span>
                    )}
                    {group.staffMember && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        {group.staffMember.name} {group.staffMember.surname}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => setAddUserOpen(true)}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    {t('classBookings.addUser', 'Add User')}
                  </Button>
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{confirmedCount}/{group.participants.length}</span>
                  </div>
                </div>
              </div>

              {/* Participants list */}
              <div className="space-y-1.5">
                {group.participants.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-1">
                    {t('classBookings.noParticipants', 'No participants yet')}
                  </p>
                ) : (
                  group.participants.map((participant) => (
                    <div
                      key={participant._id}
                      className="flex items-center justify-between py-1.5 px-2 rounded-md bg-muted/30 gap-2"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium truncate block">{participant.fullName}</span>
                        {participant.phoneNumber && (
                          <span className="text-xs text-muted-foreground">{participant.phoneNumber}</span>
                        )}
                      </div>
                      <Badge variant={getStatusVariant(participant.status)} className="text-xs shrink-0">
                        {getStatusLabel(participant.status)}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AddUserToClassDialog
        classId={group.classId}
        timeSlotId={group.timeSlotId}
        date={group.date}
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
      />
    </>
  );
}

function BookingCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex">
          <div className="bg-muted/40 px-4 py-4 min-w-[80px] border-r space-y-2">
            <Skeleton className="h-6 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
          <div className="flex-1 p-4 space-y-3">
            <div className="flex justify-between">
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-5 w-12" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function ClassBookingsPage() {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [classFilter, setClassFilter] = useState<string>('all');

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const { data: bookings = [], isLoading } = useBookingsByDay(dateStr);
  const { data: allClasses = [] } = useClasses();

  // Group flat bookings into slots, then merge empty (available) slots
  const groupedBookings = useMemo<GroupedBooking[]>(() => {
    const map: Record<string, GroupedBooking> = {};
    for (const booking of bookings) {
      const resolvedClassId =
        typeof booking.classId === 'object' && booking.classId !== null
          ? booking.classId._id
          : booking.classId;

      const key = `${resolvedClassId}-${booking.timeSlotId}`;
      if (!map[key]) {
        map[key] = {
          classId: resolvedClassId,
          timeSlotId: booking.timeSlotId,
          startTime: booking.startTime,
          endTime: booking.endTime,
          date: booking.date,
          className: booking.className,
          classLabel: booking.classLabel,
          staffMember: booking.staffMember,
          participants: [],
        };
      }
      map[key].participants.push({
        _id: booking._id,
        fullName: booking.fullName,
        phoneNumber: booking.phoneNumber,
        status: booking.status,
        createdAt: booking.createdAt,
      });
    }

    // Merge available class slots that have no bookings yet
    for (const gymClass of allClasses) {
      if (!gymClass.isActive) continue;
      if (gymClass.blackoutDates?.includes(dateStr)) continue;

      for (const slot of gymClass.timeSlots) {
        if (slot.disabled) continue;
        if (slot.disabledDates?.includes(dateStr)) continue;

        const slotId = slot._id || slot.timeSlotId;
        if (!isSlotActiveOnDate(slot, dateStr, gymClass.daysOfWeek)) continue;

        const key = `${gymClass._id}-${slotId}`;
        if (map[key]) continue; // already has bookings

        map[key] = {
          classId: gymClass._id,
          timeSlotId: slotId,
          startTime: slot.startTime,
          endTime: slot.endTime,
          date: dateStr,
          className: gymClass.name,
          classLabel: gymClass.label,
          staffMember: slot.staffMember,
          participants: [],
        };
      }
    }

    return Object.values(map).sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [bookings, allClasses, dateStr]);

  // Use all classes for the filter dropdown (sorted alphabetically)
  const sortedClasses = useMemo(
    () => [...allClasses].sort((a, b) => a.name.localeCompare(b.name)),
    [allClasses]
  );

  // Keep filter value stable; fall back to 'all' if selected class no longer exists
  const classFilterValue =
    classFilter === 'all' || sortedClasses.some((c) => c._id === classFilter)
      ? classFilter
      : 'all';

  // Apply class + status filters
  const filteredGroups = useMemo<GroupedBooking[]>(() => {
    let groups = groupedBookings;

    if (classFilterValue !== 'all') {
      groups = groups.filter((g) => g.classId === classFilterValue);
    }

    if (statusFilter !== 'all') {
      groups = groups
        .map((group) => ({
          ...group,
          participants: group.participants.filter((p) => p.status === statusFilter),
        }))
        .filter((group) => group.participants.length > 0);
    }

    return groups;
  }, [groupedBookings, classFilterValue, statusFilter, sortedClasses]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const goToPrevWeek = () => {
    const prev = subWeeks(weekStart, 1);
    setWeekStart(prev);
    setSelectedDate(prev);
  };

  const goToNextWeek = () => {
    const next = addWeeks(weekStart, 1);
    setWeekStart(next);
    setSelectedDate(next);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setWeekStart(startOfWeek(today, { weekStartsOn: 1 }));
  };

  const handleDaySelect = (day: Date) => {
    setSelectedDate(day);
  };

  const totalParticipants = groupedBookings.reduce((sum, g) => sum + g.participants.length, 0);
  const confirmedParticipants = groupedBookings.reduce(
    (sum, g) => sum + g.participants.filter((p) => p.status === 'confirmed' || p.status === 'active').length,
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title={t('classBookings.title', 'Class Bookings')}>
        <Button variant="outline" size="sm" onClick={goToToday}>
          <Calendar className="h-4 w-4 mr-2" />
          {t('classBookings.today', 'Today')}
        </Button>
      </PageHeader>

      {/* Week strip */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={goToPrevWeek} className="shrink-0">
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex flex-1 gap-1 overflow-x-auto">
              {weekDays.map((day) => {
                const isSelected = isSameDay(day, selectedDate);
                const isTodayDay = isToday(day);
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => handleDaySelect(day)}
                    className={cn(
                      'flex flex-col items-center justify-center rounded-lg px-2 py-2 min-w-[52px] flex-1 transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : isTodayDay
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <span className="text-xs font-medium uppercase tracking-wide">
                      {format(day, 'EEE')}
                    </span>
                    <span className={cn('text-xl font-bold tabular-nums mt-0.5', isTodayDay && !isSelected && 'text-primary')}>
                      {format(day, 'd')}
                    </span>
                  </button>
                );
              })}
            </div>

            <Button variant="ghost" size="icon" onClick={goToNextWeek} className="shrink-0">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selected date header + stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">
            {format(selectedDate, 'EEEE, d MMMM yyyy')}
          </h2>
          {!isLoading && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {groupedBookings.length === 0
                ? t('classBookings.noSessions', 'No sessions scheduled')
                : t(
                    'classBookings.sessionsSummary',
                    '{{sessions}} sessions · {{confirmed}} confirmed / {{total}} booked',
                    {
                      sessions: groupedBookings.length,
                      confirmed: confirmedParticipants,
                      total: totalParticipants,
                    }
                  )}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Class filter */}
          <Select
            value={classFilterValue}
            onValueChange={(val) => setClassFilter(val)}
          >
            <SelectTrigger className="h-8 w-[160px] text-sm">
              <SelectValue placeholder="All classes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {sortedClasses.map((c) => (
                <SelectItem key={c._id} value={c._id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Divider */}
          <div className="h-6 w-px bg-border" />

          {/* Status filter chips */}
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                statusFilter === filter.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bookings list */}
      <div className="space-y-3">
        {isLoading ? (
          <>
            <BookingCardSkeleton />
            <BookingCardSkeleton />
            <BookingCardSkeleton />
          </>
        ) : filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <BookingCard key={`${group.classId}-${group.timeSlotId}-${group.startTime}`} group={group} />
          ))
        ) : (
          <Card>
            <CardContent className="py-16 flex flex-col items-center text-center gap-3">
              <Calendar className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-muted-foreground text-base">
                {classFilterValue !== 'all' || statusFilter !== 'all'
                  ? t('classBookings.noFilteredBookings', 'No bookings match the selected filters')
                  : t('classBookings.noBookings', 'No bookings for this day')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
